import { RefObject, useCallback, useEffect, useState } from "react"

/** Whether there is more content past each edge of a scroll container. */
export type ScrollOverflowT = {
    top: boolean
    bottom: boolean
    left: boolean
    right: boolean
    /** width of the vertical scrollbar, so a fade can stop short of it */
    scrollbarWidth: number
    /** height of the horizontal scrollbar, likewise */
    scrollbarHeight: number
}

const NO_OVERFLOW: ScrollOverflowT = {
    top: false,
    bottom: false,
    left: false,
    right: false,
    scrollbarWidth: 0,
    scrollbarHeight: 0,
}

const sameOverflow = (a: ScrollOverflowT, b: ScrollOverflowT) =>
    (Object.keys(a) as (keyof ScrollOverflowT)[]).every((key) => a[key] === b[key])

/**
 * Track which edges of `ref`'s element have content beyond them.
 *
 * Re-checked when it scrolls, when it is resized, and when its content changes size — a
 * section expanding or collapsing, or the timeline zooming, changes what overflows without
 * the container itself changing size, so every direct child is observed as well (and
 * re-observed as they change).
 */
export const useScrollOverflow = (ref: RefObject<HTMLElement>): ScrollOverflowT => {
    const [overflow, setOverflow] = useState(NO_OVERFLOW)

    const update = useCallback(() => {
        const element = ref.current
        if (!element) return
        const next = {
            top: element.scrollTop > 1,
            bottom: element.scrollTop + element.clientHeight < element.scrollHeight - 1,
            left: element.scrollLeft > 1,
            right: element.scrollLeft + element.clientWidth < element.scrollWidth - 1,
            scrollbarWidth: element.offsetWidth - element.clientWidth,
            scrollbarHeight: element.offsetHeight - element.clientHeight,
        }
        setOverflow((previous) => (sameOverflow(previous, next) ? previous : next))
    }, [ref])

    useEffect(() => {
        const element = ref.current
        if (!element) return

        const resizeObserver = new ResizeObserver(update)
        const observeAll = () => {
            resizeObserver.disconnect()
            resizeObserver.observe(element)
            Array.from(element.children).forEach((child) => resizeObserver.observe(child))
            update()
        }
        // children come and go as the selection changes — keep watching the current ones
        const mutationObserver = new MutationObserver(observeAll)
        mutationObserver.observe(element, { childList: true })
        observeAll()

        element.addEventListener("scroll", update, { passive: true })
        return () => {
            element.removeEventListener("scroll", update)
            mutationObserver.disconnect()
            resizeObserver.disconnect()
        }
    }, [ref, update])

    return overflow
}
